import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
    Dialog,
    DialogHeader,
    DialogBody,
    DialogFooter,
    Button,
    Input,
    Select,
    Option,
    Typography,
    Checkbox
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
const { rootUrl } = require('../../../config/config');

const VendorPaymentForm = ({ open, onClose, onSuccess, supplierId, supplierName, pendingInvoices = [] }) => {
    const { token, user } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [formData, setFormData] = useState({
        invoice_ids: [],
        payment_amount: '',
        cashbook_account_id: '',
        payment_method: 'CASH',
        payment_reference: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [cashbookAccounts, setCashbookAccounts] = useState([]);

    const fetchCashbookAccounts = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/api/cashbook/accounts`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            if (response.data && response.data.success) {
                setCashbookAccounts(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching cashbook accounts:', error);
        }
    }, [authToken]);

    useEffect(() => {
        if (open && pendingInvoices) {
            // Calculate total from selected invoices
            const total = formData.invoice_ids.reduce((sum, id) => {
                const invoice = pendingInvoices.find(inv => inv.id === id);
                return sum + (invoice ? parseFloat(invoice.remaining || 0) : 0);
            }, 0);
            setFormData(prev => ({ ...prev, payment_amount: total > 0 ? total.toString() : '' }));
        }
    }, [open, formData.invoice_ids, pendingInvoices]);

    useEffect(() => {
        if (open) {
            fetchCashbookAccounts();
        }
    }, [open, fetchCashbookAccounts, pendingInvoices]);

    const handleInvoiceToggle = (invoiceId) => {
        setFormData(prev => {
            const newIds = prev.invoice_ids.includes(invoiceId)
                ? prev.invoice_ids.filter(id => id !== invoiceId)
                : [...prev.invoice_ids, invoiceId];
            
            // Recalculate total
            const total = newIds.reduce((sum, id) => {
                const invoice = pendingInvoices.find(inv => inv.id === id);
                return sum + (invoice ? parseFloat(invoice.remaining || 0) : 0);
            }, 0);
            
            return {
                ...prev,
                invoice_ids: newIds,
                payment_amount: total > 0 ? total.toString() : ''
            };
        });
    };

    const validate = () => {
        const newErrors = {};
        if (formData.invoice_ids.length === 0) {
            newErrors.invoice_ids = 'Select at least one invoice';
        }
        if (!formData.payment_amount || parseFloat(formData.payment_amount) <= 0) {
            newErrors.payment_amount = 'Payment amount must be greater than zero';
        }
        if (!formData.cashbook_account_id) {
            newErrors.cashbook_account_id = 'Cashbook account is required';
        }
        if (!formData.payment_date) {
            newErrors.payment_date = 'Payment date is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const response = await axios.post(
                `${rootUrl}/api/vendors/${supplierId}/payments`,
                {
                    ...formData,
                    payment_amount: parseFloat(formData.payment_amount),
                    input_by: user?.username || 'system'
                },
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                onSuccess && onSuccess(response.data.data);
                handleClose();
            } else {
                setErrors({ submit: response.data?.message || 'Failed to record payment' });
            }
        } catch (error) {
            setErrors({ submit: error.response?.data?.message || 'Error recording payment' });
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            invoice_ids: [],
            payment_amount: '',
            cashbook_account_id: '',
            payment_method: 'CASH',
            payment_reference: '',
            payment_date: new Date().toISOString().split('T')[0],
            notes: ''
        });
        setErrors({});
        onClose();
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(amount || 0);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-KE');
    };

    return (
        <Dialog open={open} handler={handleClose} size="lg">
            <DialogHeader>Record Vendor Payment - {supplierName}</DialogHeader>
            <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4">
                    {pendingInvoices.length > 0 ? (
                        <div className="max-h-64 overflow-y-auto border rounded p-4">
                            <Typography variant="h6" className="mb-2">Select Invoices to Pay</Typography>
                            {pendingInvoices.map((invoice) => (
                                <div key={invoice.id} className="flex items-center gap-2 p-2 border-b">
                                    <Checkbox
                                        checked={formData.invoice_ids.includes(invoice.id)}
                                        onChange={() => handleInvoiceToggle(invoice.id)}
                                    />
                                    <div className="flex-1">
                                        <Typography variant="small">
                                            Invoice: {invoice.invoice_no || 'N/A'} | 
                                            Due: {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'} |
                                            Remaining: {formatCurrency(invoice.remaining)}
                                            {invoice.is_overdue && (
                                                <span className="ml-2 text-red-600 font-bold">OVERDUE</span>
                                            )}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                            {errors.invoice_ids && (
                                <Typography color="red" variant="small">{errors.invoice_ids}</Typography>
                            )}
                        </div>
                    ) : (
                        <Typography variant="small" color="gray">
                            No pending invoices found
                        </Typography>
                    )}

                    <Select
                        label="Cashbook Account *"
                        value={formData.cashbook_account_id}
                        onChange={(val) => setFormData(prev => ({ ...prev, cashbook_account_id: val }))}
                        error={!!errors.cashbook_account_id}
                    >
                        <Option value="">Select Account</Option>
                        {cashbookAccounts.map(account => (
                            <Option key={account.id} value={account.id}>
                                {account.account_name} ({account.account_type})
                            </Option>
                        ))}
                    </Select>

                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            type="number"
                            label="Payment Amount *"
                            value={formData.payment_amount}
                            onChange={(e) => setFormData(prev => ({ ...prev, payment_amount: e.target.value }))}
                            error={!!errors.payment_amount}
                            step="0.01"
                        />
                        <Input
                            type="date"
                            label="Payment Date *"
                            value={formData.payment_date}
                            onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                            error={!!errors.payment_date}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Payment Method"
                            value={formData.payment_method}
                            onChange={(val) => setFormData(prev => ({ ...prev, payment_method: val }))}
                        >
                            <Option value="CASH">Cash</Option>
                            <Option value="BANK_TRANSFER">Bank Transfer</Option>
                            <Option value="MPESA">M-Pesa</Option>
                            <Option value="CHEQUE">Cheque</Option>
                            <Option value="CREDIT_CARD">Credit Card</Option>
                        </Select>
                        <Input
                            label="Payment Reference"
                            value={formData.payment_reference}
                            onChange={(e) => setFormData(prev => ({ ...prev, payment_reference: e.target.value }))}
                        />
                    </div>

                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                        <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={formData.notes}
                            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                            rows={3}
                        />
                    </div>

                    {errors.submit && (
                        <Typography color="red" variant="small">
                            {errors.submit}
                        </Typography>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={handleClose}
                        className="mr-2"
                    >
                        <FontAwesomeIcon icon={faTimes} className="mr-2" />
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        color="blue"
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faSave} className="mr-2" />
                        {loading ? 'Processing...' : 'Record Payment'}
                    </Button>
                </DialogFooter>
            </form>
        </Dialog>
    );
};

export default VendorPaymentForm;

